import { useMemo, useState } from 'react';
import { X, Loader2, AlertCircle, CheckCircle2, Calendar } from 'lucide-react';
import supabase from '../../lib/supabase';

interface RescheduleBookingModalProps {
  kind: 'court' | 'coach';
  bookingId: string;
  bookingReference: string;
  startAt: string;
  endAt?: string;              // required for court so we preserve duration
  sessionMinutes?: number;     // convenience for coach bookings
  onClose: () => void;
  onDone: () => void;
}

/**
 * Lets the customer pick a new date + time for a booking. Duration is
 * preserved so the price can't change. The edge function validates
 * availability, club hours, coach availability, and reschedule quota.
 */
export default function RescheduleBookingModal({
  kind, bookingId, bookingReference, startAt, endAt, sessionMinutes,
  onClose, onDone,
}: RescheduleBookingModalProps) {
  const originalStart = useMemo(() => new Date(startAt), [startAt]);
  const durationMin = useMemo(() => {
    if (kind === 'coach') return sessionMinutes ?? 60;
    if (!endAt) return 60;
    return Math.round((new Date(endAt).getTime() - originalStart.getTime()) / 60_000);
  }, [kind, endAt, sessionMinutes, originalStart]);

  // Default: same time of day, tomorrow.
  const defaultDate = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    return d.toISOString().split('T')[0];
  }, []);
  const defaultTime = useMemo(() => {
    const h = originalStart.getHours();
    const m = originalStart.getMinutes();
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
  }, [originalStart]);

  const [date, setDate] = useState(defaultDate);
  const [time, setTime] = useState(defaultTime);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const submit = async () => {
    setError(null);
    setBusy(true);
    const newStart = new Date(`${date}T${time}:00`);
    if (Number.isNaN(newStart.getTime())) {
      setBusy(false);
      setError('Invalid date or time.');
      return;
    }

    const endpoint = kind === 'court' ? 'reschedule-court-booking' : 'reschedule-coach-booking';
    const body = kind === 'court'
      ? {
          bookingId,
          newStartAt: newStart.toISOString(),
          newEndAt:   new Date(newStart.getTime() + durationMin * 60_000).toISOString(),
        }
      : {
          bookingId,
          newScheduledAt: newStart.toISOString(),
        };
    const { data, error } = await supabase.functions.invoke(endpoint, { body });
    setBusy(false);
    if (error) {
      setError((error as any).message || 'Could not reschedule.');
      return;
    }
    // Edge function may return a 4xx with a JSON body when validation fails.
    if (data && (data as any).error) {
      setError((data as any).error);
      return;
    }
    setDone(true);
  };

  if (done) {
    return (
      <Frame onClose={onClose}>
        <div className="text-center py-6">
          <div className="w-12 h-12 rounded-full bg-green-100 mx-auto mb-3 flex items-center justify-center">
            <CheckCircle2 className="w-6 h-6 text-green-700" />
          </div>
          <h3 className="text-lg font-semibold">Rescheduled</h3>
          <p className="text-sm text-muted-foreground mt-1">
            Booking <span className="font-mono">{bookingReference}</span> moved to{' '}
            <strong>{new Date(`${date}T${time}:00`).toLocaleString('en-GB', {
              day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
            })}</strong>.
          </p>
          <button
            onClick={onDone}
            className="mt-4 h-10 px-5 rounded-lg bg-[#1A6B4F] text-white text-sm"
          >
            Back to my bookings
          </button>
        </div>
      </Frame>
    );
  }

  return (
    <Frame onClose={onClose}>
      <div className="flex items-center gap-2 mb-1">
        <Calendar className="w-4 h-4 text-[#1A6B4F]" />
        <h3 className="text-lg font-semibold">Reschedule</h3>
      </div>
      <p className="text-xs text-muted-foreground mb-4">
        Booking <span className="font-mono">{bookingReference}</span> · originally{' '}
        {originalStart.toLocaleString('en-GB', {
          day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
        })} ({durationMin} min)
      </p>

      <div className="space-y-3">
        <label className="block">
          <div className="text-xs text-muted-foreground mb-1">New date</div>
          <input type="date" value={date} min={new Date().toISOString().split('T')[0]}
            onChange={(e) => setDate(e.target.value)}
            className="w-full h-11 px-3 rounded-lg border border-border text-sm" />
        </label>
        <label className="block">
          <div className="text-xs text-muted-foreground mb-1">New start time</div>
          <input type="time" value={time}
            onChange={(e) => setTime(e.target.value)}
            className="w-full h-11 px-3 rounded-lg border border-border text-sm" />
        </label>

        <div className="text-xs text-muted-foreground bg-secondary rounded-lg p-3">
          Duration stays the same ({durationMin} min), so the price doesn't change.
          Within 4 hours of your original start time, please cancel instead.
        </div>

        {error && (
          <div className="text-sm rounded-lg p-2.5 bg-red-50 border border-red-200 text-red-700 flex items-start gap-2">
            <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        <div className="flex gap-2">
          <button type="button" onClick={onClose}
            className="flex-1 h-11 rounded-lg border border-border text-sm">
            Back
          </button>
          <button type="button" onClick={submit} disabled={busy}
            className="flex-1 h-11 rounded-lg bg-[#1A6B4F] text-white text-sm flex items-center justify-center gap-1 disabled:opacity-50">
            {busy && <Loader2 className="w-4 h-4 animate-spin" />}
            Reschedule
          </button>
        </div>
      </div>
    </Frame>
  );
}

function Frame({ children, onClose }: { children: React.ReactNode; onClose: () => void }) {
  return (
    <div className="fixed inset-0 bg-black/40 flex items-end sm:items-center justify-center z-50">
      <div className="bg-card w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl p-5 relative">
        <button onClick={onClose} aria-label="Close"
          className="absolute top-3 right-3 w-8 h-8 rounded-full bg-secondary flex items-center justify-center">
          <X className="w-4 h-4" />
        </button>
        {children}
      </div>
    </div>
  );
}
